import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getOriginationDetail,
  type OriginationDetail,
  type OriginationSummary,
} from "../api/deferred-originations";
import { useOriginationSSE } from "./useOriginationSSE";
import { useSenderOperationRefresh } from "./useSenderOperationRefresh";

export type WatchedOriginationStatus = "loading" | "ready" | "missing";

export interface UseWatchedOriginationResult {
  detail: OriginationDetail | null;
  status: WatchedOriginationStatus;
  refresh: () => Promise<void>;
}

const toSummary = (detail: OriginationDetail): OriginationSummary => ({
  originationId: detail.originationId,
  bindingState: detail.bindingState,
  sendAmount: detail.sendCommitment.sendAmount,
  sendCurrency: detail.sendCommitment.sendCurrency,
  destinationCurrency: detail.sendCommitment.destinationCurrency,
  recipientHint: detail.recipientHint,
  payoutClaimUrl: detail.payoutClaimUrl,
  operationId: detail.operationId,
  operationStatus: detail.operation?.status ?? null,
  fundingMethod:
    detail.operation?.fundingMethod ?? detail.sendCommitment.fundingMethod,
  fundingStatus: detail.operation?.fundingStatus ?? null,
  createdAt: detail.createdAt,
});

/**
 * Loads and live-tracks a deferred payout origination for the sender receipt.
 */
export function useWatchedOrigination(
  linkId: string | null | undefined,
  originationId: string | null | undefined
): UseWatchedOriginationResult {
  const [detail, setDetail] = useState<OriginationDetail | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const summaries = useMemo(
    () => (detail ? [toSummary(detail)] : []),
    [detail]
  );

  const refreshDetail = useCallback(async () => {
    if (!linkId || !originationId) return;
    try {
      const next = await getOriginationDetail(linkId, originationId);
      setDetail(next);
    } catch (error) {
      console.error(
        "[useWatchedOrigination] Failed to refresh origination:",
        error
      );
    }
  }, [linkId, originationId]);

  const handleSseUpsert = useCallback(
    (item: OriginationSummary) => {
      if (item.originationId !== originationId) return;
      void refreshDetail();
    },
    [originationId, refreshDetail]
  );

  useOriginationSSE(
    linkId,
    summaries,
    handleSseUpsert,
    refreshDetail,
    { watchOriginationId: originationId ?? undefined }
  );

  // Keep listening after settle: EXECUTION_ENRICHED can land late (PIN / PDF).
  const watchSenderOperation = useMemo(
    () => Boolean(detail?.operationId),
    [detail?.operationId]
  );

  useSenderOperationRefresh(linkId, watchSenderOperation, refreshDetail);

  useEffect(() => {
    if (!linkId || !originationId) {
      setDetail(null);
      setInitialLoadDone(false);
      return;
    }

    let cancelled = false;
    setInitialLoadDone(false);
    setDetail(null);

    getOriginationDetail(linkId, originationId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((error) => {
        console.error(
          "[useWatchedOrigination] Failed to fetch origination:",
          error
        );
      })
      .finally(() => {
        if (!cancelled) setInitialLoadDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [linkId, originationId]);

  let status: WatchedOriginationStatus;
  if (detail) {
    status = "ready";
  } else if (!initialLoadDone) {
    status = "loading";
  } else {
    status = "missing";
  }

  return { detail, status, refresh: refreshDetail };
}
